import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import Toggle from "../common/Toggle";
import Button from "../common/Button";
import SoilLayerTableItem from "./SoilLayerTableItem";
import { LOCAL_PREFIX } from "../../utils/constants";

export const MAX_SOIL_LAYERS = 3;

const SoilLayerTable = forwardRef(({
  sample, checkDirty, formProblems, setFormProblems, setAndClearProblem,
}, ref) => {
  const localIdTracker = useRef(0);

  const [isComposite, setIsComposite] = useState(sample.isComposite);
  const [layers, setLayers] = useState(() => {
    if (!sample.layers || sample.layers.length === 0) {
      const id = localIdTracker.current;
      localIdTracker.current += 1;
      return [{ id: `${LOCAL_PREFIX}-${id}`, top: 0, bottom: '' }];
    }

    // Can't alter the originals, since we need to be able to compare against them.
    return JSON.parse(JSON.stringify(sample.layers)).sort((a, b) => a.layerNumber - b.layerNumber);
  });

  // Replace our known layers if the sample layers change (usually after a save).
  useEffect(() => {
    if (!sample.layers || sample.layers.length === 0) return
    setLayers(JSON.parse(JSON.stringify(sample.layers)).sort((a, b) => a.layerNumber - b.layerNumber));
  }, [sample.layers]);

  useImperativeHandle(ref, () => ({
    isComposite,
    layers,
  }), [isComposite, layers]);

  useEffect(() => checkDirty(), [isComposite, layers]);

  return (
    <div className="bg-slate-200 p-3 rounded mt-2">
      <h3 className="font-bold text-base">
        Soil Layer Information
      </h3>
      <p>
        Add the number of soil layers that are in your test. If you have only one composite layer please check composite box and use only first layer.
      </p>
      <div className="flex justify-between">
        <Toggle label="Composite Sample" value={isComposite} onChange={setIsComposite} contrast />
        <Button
          onClick={() => setLayers((l) => {
            const result = [
              ...l,
              {
                id: `${LOCAL_PREFIX}-${localIdTracker.current}`,
                top: l[l.length - 1].bottom,
                bottom: '',
              },
            ];

            localIdTracker.current += 1;

            return result;
          })}
          disabled={layers.length >= MAX_SOIL_LAYERS || (isComposite && layers.length > 0)}
        >
          Add Layer
        </Button>
      </div>

      <div className={`rounded-lg overflow-clip mt-4 shadow border border-gray-300 bg-slate-100`}>
        <div className="flex bg-gray-100 text-zinc-600 uppercase text-center">
          <div className="w-28 py-2">Layer</div>
          <div className="flex-1 py-2">Top Depth (IN)</div>
          <div className="flex-1 py-2">Bottom Depth (IN)</div>
          <div className="w-10" />
        </div>

        {layers.filter((_, i) => isComposite ? i === 0 : true).map((item, index) => (
          <SoilLayerTableItem
            key={`soil_${sample.id}_layer_item_${item.id}`}
            sampleId={sample.id}
            index={index}
            item={item}
            setAndClearProblem={setAndClearProblem}
            onChange={(update) => {
              // No need to dispatch this.
              layers[index] = update;
              checkDirty();
            }}
            onRemove={() => {
              // Remove related formProblems.
              setFormProblems(formProblems.filter((p) => !p.field?.startsWith(`soil-${sample.id}-layer-${index + 1}`)));
              setLayers((og) => og.toSpliced(index, 1));
            }}
            formProblems={formProblems}
          />
        ))}

        {isComposite && <p className="text-sm text-stone-500 text-center w-full p-0.5">
          Additional layers disabled for composite sample
        </p>}
      </div>
    </div>
  );
});

export default SoilLayerTable;
